import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { QuestTabType } from './types/tabs';
import { TabNavigation, type TabConfig } from '../../components/TabNavigation';
import { QuestTemplates } from './components/QuestTemplates';
import { QuestResults } from './components/QuestResults';
import { BulkJourneyUpdate } from './components/BulkJourneyUpdate';
import { JourneyLeaderboard } from './components/JourneyLeaderboard';
import { cleanupOldTests } from './utils/testStorageUtils';

const tabs: TabConfig<QuestTabType>[] = [
    { id: 'templates', label: 'Quest Templates', icon: '📝' },
    { id: 'results', label: 'Quest Results', icon: '📊' },
    { id: 'bulk-journey-update', label: 'Bulk Journey Update', icon: '👥' },
    { id: 'journey-leaderboard', label: 'Journey Leaderboard', icon: '🏆' }
];

const isValidTab = (tab: string | null): tab is QuestTabType => {
    return tab === 'templates' || tab === 'results' || tab === 'bulk-journey-update' || tab === 'journey-leaderboard';
};

export const Quests = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<QuestTabType>('templates');

    // Clean up old test runs on page load
    useEffect(() => {
        cleanupOldTests();
    }, []);

    // Initialize tab from URL on mount
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (isValidTab(tabParam)) {
            setActiveTab(tabParam);
        } else if (tabParam) {
            // Invalid tab in URL, reset to templates
            setSearchParams({ tab: 'templates' }, { replace: true });
        } else {
            // No tab in URL, set default
            setSearchParams({ tab: 'templates' }, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleTabChange = (tab: QuestTabType) => {
        setActiveTab(tab);
        setSearchParams({ tab });
    };

    return (
        <div className="min-h-screen bg-gray-50 py-4 md:py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-6 md:mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Quests</h1>
                </div>

                {/* Tab Navigation */}
                <TabNavigation
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />

                {/* Quest Templates Tab */}
                {activeTab === 'templates' && <QuestTemplates />}

                {/* Quest Results Tab */}
                {activeTab === 'results' && <QuestResults />}

                {/* Bulk Journey Update Tab */}
                {activeTab === 'bulk-journey-update' && <BulkJourneyUpdate />}

                {/* Journey Leaderboard Tab */}
                {activeTab === 'journey-leaderboard' && <JourneyLeaderboard />}
            </div>
        </div>
    );
};